import { CumulativeBoosterID } from "./cumulative_booster_id.js";

export type CumulativeBoosterCountListener = (
  id: CumulativeBoosterID,
  count: number
) => void;

/** Stores how many times each booster can still be used. */
export class CumulativeBoosterInventory
{
  private readonly counts = new Map<CumulativeBoosterID, number>();
  private readonly listeners = new Set<CumulativeBoosterCountListener>();

  public onCountChanged: ( id: CumulativeBoosterID, count: number ) => void = () => {};

  public getCount( id: CumulativeBoosterID ): number
  {
    return this.counts.get( id ) ?? 0;
  }

  /** Replaces a count, for example after loading it from player storage. */
  public setCount( id: CumulativeBoosterID, count: number ): void
  {
    const normalized_count = this.normalizeCount( count );
    const previous_count = this.getCount( id );

    if ( normalized_count === previous_count )
      return;

    this.counts.set( id, normalized_count );
    this.onCountChanged( id, normalized_count );
    for ( const listener of this.listeners )
      listener( id, normalized_count );
  }

  /** Adds purchased or rewarded uses before or during a level. */
  public add( id: CumulativeBoosterID, amount: number ): void
  {
    const normalized_amount = this.normalizeCount( amount );
    if ( normalized_amount === 0 )
      return;

    this.setCount( id, this.getCount( id ) + normalized_amount );
  }

  public tryConsume( id: CumulativeBoosterID ): boolean
  {
    const count = this.getCount( id );
    if ( count === 0 )
      return false;

    this.setCount( id, count - 1 );
    return true;
  }

  public subscribe( listener: CumulativeBoosterCountListener ): () => void
  {
    this.listeners.add( listener );
    return () => this.listeners.delete( listener );
  }

  private normalizeCount( count: number ): number
  {
    if ( !Number.isFinite( count ) || count < 0 )
      throw new Error( "Booster count must be a finite non-negative number" );

    return Math.floor( count );
  }
}
